import React, { useEffect, useState } from 'react';
import { Box, Card, Heading, Spinner, Text, Flex, Button, Link, Icon, useToast, VStack, Grid, GridItem } from '@chakra-ui/react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowBackIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import Axios from '../../utils/httpClient';
import Layout from '../../sections/Layout';
import { FILE_URL } from '../../utils/api';

const PRIMARY = '#133654';

// Helper function to render field value (makes URLs clickable)
const renderFieldValue = (field) => {
    const isUrl = field.label && (field.label.includes('manzil') || field.label.includes('Manzil') || field.label.includes('URL'));

    if (isUrl && field.value) {
        // Add https:// if URL doesn't have a protocol
        const url = field.value.startsWith('http://') || field.value.startsWith('https://')
            ? field.value
            : `https://${field.value}`;

        return (
            <Text
                color="blue.600"
                textDecoration="underline"
                cursor="pointer"
                onClick={() => window.open(url, '_blank')}
                _hover={{ color: 'blue.800' }}
            >
                {field.value}
            </Text>
        );
    }
    return <Text>{field.value}</Text>;
};

const statusBg = (s, app) => {
    const code = app?.parameter?.code || app?.parameter?.code;

    // Multi-department approval logic
    if (code === '1.1' && (app?.tt_status !== undefined || app?.ts_status !== undefined || app?.ov_status !== undefined)) {
        const hasRejection = app.tt_status === 2 || app.ts_status === 2 || app.ov_status === 2;
        const allApproved = app.tt_status === 1 && app.ts_status === 1 && app.ov_status === 1;

        if (hasRejection) return 'rgba(231, 76, 60, 0.15)'; // Red for rejected
        if (allApproved) return 'rgba(46, 204, 113, 0.15)'; // Green for approved
        return 'rgba(241, 196, 15, 0.25)'; // Yellow for in progress
    }

    if (code === '1.3' || code === '1.4') {
        const hasRejection = app.mn_status === 2 || app.ov_status === 2;
        const allApproved = app.mn_status === 1 && app.ov_status === 1;

        if (hasRejection) return 'rgba(231, 76, 60, 0.15)';
        if (allApproved) return 'rgba(46, 204, 113, 0.15)';
        return 'rgba(241, 196, 15, 0.25)';
    }

    if (code === '3.1') {
        const hasRejection = app.tt_status === 2 || app.ts_status === 2;
        const allApproved = app.tt_status === 1 && app.ts_status === 1;

        if (hasRejection) return 'rgba(231, 76, 60, 0.15)';
        if (allApproved) return 'rgba(46, 204, 113, 0.15)';
        return 'rgba(241, 196, 15, 0.25)';
    }

    if (code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9') {
        // Single department approval (O'quv bo'limi)
        if (s === 5) return 'rgba(231, 76, 60, 0.15)';
        if (s === 2 || s === 3 || s === 4) return 'rgba(46, 204, 113, 0.15)';
        return 'rgba(241, 196, 15, 0.25)';
    }

    if (String(code).startsWith('2.')) {
        // Single department approval (Ilmiy tadqiqotlar bo'limi)
        if (s === 5) return 'rgba(231, 76, 60, 0.15)';
        if (s === 2 || s === 3 || s === 4) return 'rgba(46, 204, 113, 0.15)';
        return 'rgba(241, 196, 15, 0.25)';
    }

    if (code === '3.2' || code === '3.3') {
        const hasRejection = app.ov_status === 2 || app.tt_status === 2 || app.ts_status === 2;
        const allApproved = app.ov_status === 1 && app.tt_status === 1 && app.ts_status === 1;

        if (hasRejection) return 'rgba(231, 76, 60, 0.15)';
        if (allApproved) return 'rgba(46, 204, 113, 0.15)';
        return 'rgba(241, 196, 15, 0.25)';
    }

    // Default status logic
    if (s === 5) return 'rgba(231, 76, 60, 0.15)';
    if (s === 2 || s === 3 || s === 4) return 'rgba(46, 204, 113, 0.15)';
    return 'rgba(241, 196, 15, 0.25)';
};

const renderWork = (app) => {
    const appParamCode = app?.parameter?.code;

    if (String(appParamCode) === '1.1') {
        try {
            const d = JSON.parse(app.work || '{}');
            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        <Text fontWeight={600}>Fan nomi</Text>
                        <Text>{d?.name || '-'}</Text>
                        <Text fontWeight={600}>Mavzu nomi</Text>
                        <Text>{d?.topicName || '-'}</Text>
                        <Text fontWeight={600}>Mualliflar soni</Text>
                        <Text>{d?.authorsCount || '-'}</Text>
                        <Text fontWeight={600}>Tasdiqlangan sana</Text>
                        <Text>{d?.approvedDate || '-'}</Text>
                        <Text fontWeight={600}>Platforma nomi</Text>
                        <Text>{d?.platformName || '-'}</Text>
                        <Text fontWeight={600}>Internet manzili</Text>
                        {d?.internetAddress ? (
                            <Text
                                color="blue.600"
                                textDecoration="underline"
                                cursor="pointer"
                                onClick={() => {
                                    const url = d.internetAddress.startsWith('http') ? d.internetAddress : `https://${d.internetAddress}`;
                                    window.open(url, '_blank');
                                }}
                            >
                                {d.internetAddress}
                            </Text>
                        ) : (
                            <Text>-</Text>
                        )}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.2') {
        try {
            const d = JSON.parse(app.work || '{}');
            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        <Text fontWeight={600}>Ta'lim yo'nalishi</Text>
                        <Text>{d?.educationDirection || '-'}</Text>
                        <Text fontWeight={600}>Guruhlar</Text>
                        <Text>{d?.groups || '-'}</Text>
                        <Text fontWeight={600}>Jami talabalar soni</Text>
                        <Text>{d?.totalStudents || '-'}</Text>
                        <Text fontWeight={600}>Monitoring talabalar soni</Text>
                        <Text>{d?.monitoringStudents || '-'}</Text>
                        <Text fontWeight={600}>O'rtacha o'zlashtirish</Text>
                        <Text>{d?.averageScore || '-'}</Text>
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.3') {
        try {
            const d = JSON.parse(app.work || '{}');
            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        <Text fontWeight={600}>Nomi va turi</Text>
                        <Text>{d?.name || '-'}</Text>
                        <Text fontWeight={600}>Talabaning FISh</Text>
                        <Text>{d?.studentInfo || '-'}</Text>
                        <Text fontWeight={600}>Joyi, sanasi</Text>
                        <Text>{d?.location || '-'}</Text>
                        <Text fontWeight={600}>Egallagan o'rni</Text>
                        <Text>{d?.achievement || '-'}</Text>
                        <Text fontWeight={600}>Diplom seriyasi</Text>
                        <Text>{d?.diplomaSerial || '-'}</Text>
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.4') {
        try {
            const d = JSON.parse(app.work || '{}');
            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        <Text fontWeight={600}>Nomi va turi</Text>
                        <Text>{d?.name || '-'}</Text>
                        <Text fontWeight={600}>Talabaning FISh</Text>
                        <Text>{d?.studentInfo || '-'}</Text>
                        <Text fontWeight={600}>Joyi, sanasi</Text>
                        <Text>{d?.location || '-'}</Text>
                        <Text fontWeight={600}>Egallagan o'rni</Text>
                        <Text>{d?.achievement || '-'}</Text>
                        <Text fontWeight={600}>Diplom seriyasi</Text>
                        <Text>{d?.diplomaSerial || '-'}</Text>
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.6') {
        try {
            const d = JSON.parse(app.work || '{}');
            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        <Text fontWeight={600}>Nomi</Text>
                        <Text>{d?.name || '-'}</Text>
                        <Text fontWeight={600}>Nashr etilgan vaqti</Text>
                        <Text>{d?.publishTime || '-'}</Text>
                        <Text fontWeight={600}>Mualliflar soni</Text>
                        <Text>{d?.authorsCount || '-'}</Text>
                        <Text fontWeight={600}>Xorijlik mualliflar soni</Text>
                        <Text>{d?.foreignAuthors || '-'}</Text>
                        <Text fontWeight={600}>Tili</Text>
                        <Text>{d?.language || '-'}</Text>
                        <Text fontWeight={600}>Nashr parametlari (nashriyot, sahifasi)</Text>
                        <Text>{d?.publishParams || '-'}</Text>
                        <Text fontWeight={600}>Turi</Text>
                        <Text>{d?.direction || '-'}</Text>
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.7') {
        try {
            const d = JSON.parse(app.work || '{}');
            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        <Text fontWeight={600}>Nomi</Text>
                        <Text>{d?.name || '-'}</Text>
                        <Text fontWeight={600}>Nashr etilgan vaqti</Text>
                        <Text>{d?.publishTime || '-'}</Text>
                        <Text fontWeight={600}>Mualliflar soni</Text>
                        <Text>{d?.authorsCount || '-'}</Text>
                        <Text fontWeight={600}>Xorijlik mualliflar soni</Text>
                        <Text>{d?.foreignAuthors || '-'}</Text>
                        <Text fontWeight={600}>Tili</Text>
                        <Text>{d?.language || '-'}</Text>
                        <Text fontWeight={600}>Nashr parametlari</Text>
                        <Text>{d?.publishParams || '-'}</Text>
                        <Text fontWeight={600}>Yo'nalishi</Text>
                        <Text>{d?.direction || '-'}</Text>
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.8') {
        try {
            const d = JSON.parse(app.work || '{}');
            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        <Text fontWeight={600}>Sertifikat turi</Text>
                        <Text>{d?.certType || '-'}</Text>
                        <Text fontWeight={600}>Daraja</Text>
                        <Text>{d?.level || '-'}</Text>
                        <Text fontWeight={600}>Sana</Text>
                        <Text>{d?.date || '-'}</Text>
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '1.9') {
        try {
            const d = JSON.parse(app.work || '{}');
            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        <Text fontWeight={600}>Tur</Text>
                        <Text>{d?.type || '-'}</Text>
                        <Text fontWeight={600}>Joyi</Text>
                        <Text>{d?.location || '-'}</Text>
                        <Text fontWeight={600}>Boshlanish sanasi</Text>
                        <Text>{d?.startDate || '-'}</Text>
                        <Text fontWeight={600}>Tugash sanasi</Text>
                        <Text>{d?.endDate || '-'}</Text>
                        <Text fontWeight={600}>Yo'nalishi</Text>
                        <Text>{d?.direction || '-'}</Text>
                        <Text fontWeight={600}>Maqsadi</Text>
                        <Text>{d?.purpose || '-'}</Text>
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '2.1') {
        try {
            const d = JSON.parse(app.work || '{}');
            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        <Text fontWeight={600}>Daraja turi</Text>
                        <Text>{d?.degreeType || '-'}</Text>
                        <Text fontWeight={600}>Diplom seriyasi</Text>
                        <Text>{d?.diplomaSerial || '-'}</Text>
                        <Text fontWeight={600}>Diplom raqami</Text>
                        <Text>{d?.diplomaNumber || '-'}</Text>
                        <Text fontWeight={600}>Berilgan sana</Text>
                        <Text>{d?.diplomaDate || '-'}</Text>
                        <Text fontWeight={600}>Ixtisoslik</Text>
                        <Text>{d?.specialty || '-'}</Text>
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '2.2') {
        try {
            const d = JSON.parse(app.work || '{}');
            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        <Text fontWeight={600}>Mutaxassislik kodi</Text>
                        <Text>{d?.specialtyCode || '-'}</Text>
                        <Text fontWeight={600}>Mutaxassislik nomi</Text>
                        <Text>{d?.specialtyName || '-'}</Text>
                        <Text fontWeight={600}>Unvon berilgan sana</Text>
                        <Text>{d?.titleDate || '-'}</Text>
                        <Text fontWeight={600}>Unvon guvohnomasi seriyasi</Text>
                        <Text>{d?.titleSerial || '-'}</Text>
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '2.3') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Shogirdining FISHi', value: d?.studentName || d?.pupilFullName || d?.pupilName23 },
                { label: 'Shogirdining lavozimi', value: d?.studentPosition || d?.pupilPosition || d?.pupilPosition23 },
                { label: 'Ish bajarilgan tashkilot', value: d?.organization || d?.org23 },
                { label: 'Himoya sanasi', value: d?.defenseDate || d?.defenseDate23 },
                { label: 'Ixtisosligi shifri va nomi', value: d?.specialtyCode && d?.specialtyName ? `${d.specialtyCode} - ${d.specialtyName}` : (d?.specialtyCodeName || d?.specCodeName23 || d?.specialtyCode || d?.specialtyName) }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) {
            console.error('Error parsing KPI 2.3 data:', e, 'work:', app?.work);
            return <Text>{app?.work}</Text>;
        }
    } else if (String(appParamCode) === '2.4') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Jurnal nomi', value: d?.journalName },
                { label: 'Xalqaro ilmiy-texnik baza nomi', value: d?.database },
                { label: 'Maqolaning nomi', value: d?.articleName },
                { label: 'Nashr etilgan vaqti', value: d?.publishDate },
                { label: 'Maqola chop etilgan davlat', value: d?.country },
                { label: 'Mualliflar soni', value: d?.authorsCount },
                { label: 'Xorijlik mualliflar soni', value: d?.foreignAuthors },
                { label: 'Maqolaning chop etilgan tili', value: d?.articleLang },
                { label: 'Nashr parametrlari (jurnal soni, sahifasi)', value: d?.publishParams },
                { label: 'Profil manzili', value: d?.profileUrl }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                {renderFieldValue(field)}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '2.5') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Jurnal nomi', value: d?.journalName },
                { label: 'OAK ro\'yxati', value: d?.oakRegistry },
                { label: 'Maqolaning nomi', value: d?.articleName },
                { label: 'Nashr etilgan vaqti', value: d?.publishDate },
                { label: 'Maqola chop etilgan davlat', value: d?.country },
                { label: 'Mualliflar soni', value: d?.authorsCount },
                { label: 'Xorijlik mualliflar soni', value: d?.foreignAuthors },
                { label: 'Maqolaning chop etilgan tili', value: d?.articleLang },
                { label: 'Nashr parametrlari (jurnal soni, sahifasi)', value: d?.publishParams },
                { label: 'Maqola manzili', value: d?.articleUrl }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                {renderFieldValue(field)}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '2.6') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Ma\'lumot bazasi', value: d?.database },
                { label: 'Profil manzili', value: d?.profileUrl },
                { label: 'Maqolalar soni', value: d?.articleCount },
                { label: 'H-indeks', value: d?.hIndex }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                {renderFieldValue(field)}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) {
            console.error('Error parsing KPI 2.6 data:', e, 'work:', app?.work);
            return <Text>{app?.work}</Text>;
        }
    } else if (String(appParamCode) === '2.7') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Jurnal nomi', value: d?.journalName },
                { label: 'OAC ro\'yxati', value: d?.oakRegistry },
                { label: 'Maqolaning nomi', value: d?.articleName },
                { label: 'Nashr etilgan vaqti', value: d?.publishDate },
                { label: 'Mualliflar soni', value: d?.authorsCount },
                { label: 'Xorijlik mualliflar soni', value: d?.foreignAuthors },
                { label: 'Maqolaning chop etilgan tili', value: d?.articleLang },
                { label: 'Nashr parametrlari', value: d?.publishParams },
                { label: 'Maqola manzili', value: d?.articleUrl }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                {renderFieldValue(field)}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) {
            console.error('Error parsing KPI 2.7 data:', e, 'work:', app?.work);
            return <Text>{app?.work}</Text>;
        }
    } else if (String(appParamCode) === '2.8') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Patent nomi', value: d?.patentName },
                { label: 'Patent turi', value: d?.patentType },
                { label: 'Patent sanasi', value: d?.patentDate },
                { label: 'Ro\'yxatga olish raqami', value: d?.registrationNumber },
                { label: 'Egalar soni', value: d?.ownersCount }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) {
            console.error('Error parsing KPI 2.8 data:', e, 'work:', app?.work);
            return <Text>{app?.work}</Text>;
        }
    } else if (String(appParamCode) === '2.9') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Loyiha nomi', value: d?.projectName },
                { label: 'Shartnoma sanasi', value: d?.contractDate },
                { label: 'Umumiy summa', value: d?.totalAmount },
                { label: 'Ishtirokchilar soni', value: d?.participantsCount },
                { label: 'Olingan summa', value: d?.receivedAmount }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) {
            console.error('Error parsing KPI 2.9 data:', e, 'work:', app?.work);
            return <Text>{app?.work}</Text>;
        }
    } else if (String(appParamCode) === '2.10') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Grant nomi', value: d?.grantName },
                { label: 'Grant turi', value: d?.grantType },
                { label: 'Loyiha raqami', value: d?.projectNumber },
                { label: 'Shartnoma raqami', value: d?.contractNumber },
                { label: 'Loyiha davomiyligi', value: d?.projectDuration },
                { label: 'Umumiy summa', value: d?.totalSum },
                { label: 'Ishtirokchilar soni', value: d?.participantsCount }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) {
            console.error('Error parsing KPI 2.10 data:', e, 'work:', app?.work);
            return <Text>{app?.work}</Text>;
        }
    } else if (String(appParamCode) === '2.11') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Kengash nomi', value: d?.councilName },
                { label: 'Tashkilot nomi', value: d?.organizationName },
                { label: 'Mutaxassislik', value: d?.specialty },
                { label: 'Lavozim', value: d?.position },
                { label: 'A\'zolik sanasi', value: d?.membershipDate }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) {
            console.error('Error parsing KPI 2.11 data:', e, 'work:', app?.work);
            return <Text>{app?.work}</Text>;
        }
    } else if (String(appParamCode) === '3.1') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Xorijiy davlat', value: d?.foreignCountry },
                { label: 'Xorijiy OTM nomi', value: d?.foreignUniversity },
                { label: 'Vaqti', value: d?.degreeDate },
                { label: 'Ilmiy daraja bosqichi (PhD/DSc)', value: d?.degreeLevel },
                { label: 'Diplomining seriyasi va raqami', value: d?.diplomaSerial },
                { label: 'Ixtisosligi nomi', value: d?.specialtyName }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) {
            console.error('Error parsing KPI 3.1 data:', e, 'work:', app?.work);
            return <Text>{app?.work}</Text>;
        }
    } else if (String(appParamCode) === '3.2') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Xorijiy davlat nomi', value: d?.foreignCountry },
                { label: 'Xorijiy OTM nomi', value: d?.foreignUniversity },
                { label: 'Ixtisosligi nomi', value: d?.specialtyName },
                { label: 'O\‘quv mashg‘ulot turi', value: d?.lectureType },
                { label: 'Vaqti (boshlanishi va tugashi (kalendar))', value: d?.duration }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) {
            console.error('Error parsing KPI 3.2 data:', e, 'work:', app?.work);
            return <Text>{app?.work}</Text>;
        }
    } else if (String(appParamCode) === '3.3') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Turi', value: d?.trainingType },
                { label: 'Joyi', value: d?.trainingLocation },
                { label: 'Vaqti', value: d?.trainingDate },
                { label: 'Yo‘nalish', value: d?.trainingDirection },
                { label: 'Maqsadi', value: d?.trainingPurpose }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '3.4') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Nomi', value: d?.cooperationName },
                { label: 'Joyi', value: d?.cooperationLocation },
                { label: 'Vaqti', value: d?.cooperationDate },
                { label: 'Yo‘nalish', value: d?.cooperationDirection },
                { label: 'Maqsadi', value: d?.cooperationPurpose }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.1') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Nomi', value: d?.eventName },
                { label: 'Vaqti', value: d?.eventDate },
                { label: 'Joy', value: d?.eventLocation }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.3') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Nomi', value: d?.activityName },
                { label: 'Vaqti', value: d?.activityDate },
                { label: 'Joy', value: d?.activityLocation }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.4') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Nomi', value: d?.projectName },
                { label: 'Vaqti', value: d?.projectDate },
                { label: 'Joy', value: d?.projectLocation }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.2') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Nomi', value: d?.clubName },
                { label: 'Nizomi', value: d?.clubRegulations }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.5') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Nomi', value: d?.competitionName },
                { label: 'Turi', value: d?.competitionType },
                { label: 'Talabaning FISHi', value: d?.studentFish },
                { label: 'Joy', value: d?.competitionLocation },
                { label: 'Vaqti', value: d?.competitionDate },
                { label: 'Egallagan o\'rni', value: d?.achievement },
                { label: 'Diplom seriyasi va raqami', value: d?.diplomaSerial }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.6') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Mavzu', value: d?.mediaSubject },
                { label: 'Vaqti', value: d?.mediaDate },
                { label: 'OAV, ijtimoiy tarmoq', value: d?.mediaName }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else if (String(appParamCode) === '4.7') {
        try {
            const d = JSON.parse(app.work || '{}');
            const fields = [
                { label: 'Nomi', value: d?.name },
                { label: 'Vaqti', value: d?.time },
                { label: 'Hisobot', value: d?.report }
            ].filter(field => field.value && field.value !== '' && field.value !== null && field.value !== undefined);

            return (
                <Box>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                        {fields.map((field, idx) => (
                            <React.Fragment key={idx}>
                                <Text fontWeight={600}>{field.label}</Text>
                                <Text>{field.value}</Text>
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            );
        } catch (e) { return <Text>{app?.work}</Text>; }
    } else {
        return <Text whiteSpace="pre-wrap">{app?.work}</Text>;
    }
};

// Check if application is a kafedra-based point assignment
const isKafedraPoint = (app) => {
    const workText = String(app?.work || '');
    // Check for kafedra point indicators (both old text format and new JSON format)
    return workText.includes("Bo'lim boshlig'i:") ||
        workText.includes("Bo'lim boshlig'i tomonidan berildi (kafedra bahosi)") ||
        workText.includes("Kafedra:") ||
        workText.includes('"type": "kafedra_score"') ||
        workText.includes('"type":"kafedra_score"');
};

const renderDeptStripe = (app) => {
    try {
        const code = String(app?.parameter?.code || '');
        const items = [];
        const statusLabel = (v) => (v === 1 ? 'Tasdiqlandi' : v === 2 ? 'Rad etildi' : 'Jarayonda');
        if (code === '1.1') {
            items.push({ name: "Tarmoq texnologiyalari bo'limi", st: app.tt_status });
            items.push({ name: "Ta'lim sifati bo'limi", st: app.ts_status });
            items.push({ name: "O'quv bo'limi", st: app.ov_status });
        } else if (code === '1.3' || code === '1.4') {
            items.push({ name: "Ma'naviyat bo'limi", st: app.mn_status });
            items.push({ name: "O'quv bo'limi", st: app.ov_status });
        } else if (code === '1.6' || code === '1.7' || code === '1.8' || code === '1.9') {
            // For single-department parameters, use a different status display logic
            const singleDeptStatusLabel = (s) => {
                if (s === 5) return 'Rad etildi';  // Rejected
                if (s === 2 || s === 3 || s === 4) return 'Tasdiqlandi';  // Approved
                return 'Jarayonda';  // In progress
            };
            items.push({ name: "O'quv bo'limi", st: app.status, customLabel: singleDeptStatusLabel(app.status) });
        } else if (code === '3.1') {
            items.push({ name: "Xalqaro hamkorlik bo'limi", st: app.tt_status });
            items.push({ name: "Ilmiy bo'lim", st: app.ts_status });
        } else if (code === '3.2' || code === '3.3') {
            items.push({ name: "Xalqaro hamkorlik bo'limi", st: app.tt_status });
            items.push({ name: "Ilmiy bo'lim", st: app.ts_status });
            items.push({ name: "O'quv bo'limi", st: app.ov_status });
        } else if (code === '3.4') {
            items.push({ name: "Xalqaro hamkorlik bo'limi", st: app.tt_status });
            items.push({ name: "Ilmiy bo'lim", st: app.ts_status });
        } else if (code === '4.7') {
            // 4.7 is now single-department like other 4.x parameters → Ma'naviyat bo'limi
            const singleDeptStatusLabel = (s) => {
                if (s === 5) return 'Rad etildi';
                if (s === 2 || s === 3 || s === 4) return 'Tasdiqlandi';
                return 'Jarayonda';
            };
            items.push({ name: "Ma'naviyat bo'limi", st: app.status, customLabel: singleDeptStatusLabel(app.status) });
        } else if (code.startsWith('2.')) {
            // All other 2.x parameters (2.1–2.11) are single-department → Ilmiy tadqiqotlar
            // For 2.1, don't show status stripe (informational only)
            if (code !== '2.1') {
                const singleDeptStatusLabel = (s) => {
                    if (s === 5) return 'Rad etildi';
                    if (s === 2 || s === 3 || s === 4) return 'Tasdiqlandi';
                    return 'Jarayonda';
                };
                items.push({ name: "Ilmiy bo'lim", st: app.status, customLabel: singleDeptStatusLabel(app.status) });
            }
        } else if (code.startsWith('4.')) {
            // All 4.x parameters are single-department → Ma'naviyat bo'limi
            const singleDeptStatusLabel = (s) => {
                if (s === 5) return 'Rad etildi';
                if (s === 2 || s === 3 || s === 4) return 'Tasdiqlandi';
                return 'Jarayonda';
            };
            items.push({ name: "Ma'naviyat bo'limi", st: app.status, customLabel: singleDeptStatusLabel(app.status) });
        } else {
            // Single department – show assigned partition if present
            if (app.partition__name) {
                items.push({ name: app.partition__name, st: app.status });
            }
        }
        if (!items.length) return null;

        return (
            <Box mt={2}>
                <Text fontWeight={600} fontSize="sm" color="gray.700" mb={2}>Natija:</Text>
                <Box display="flex" flexDirection="column" gap={2}>
                    {items.map((it, idx) => {
                        const status = it.customLabel || statusLabel(it.st);
                        const isApproved = status === 'Tasdiqlandi';
                        const isRejected = status === 'Rad etildi';
                        const isInProgress = status === 'Jarayonda';

                        // Set colors based on status
                        let bgColor, borderColor, textColor;
                        if (isApproved) {
                            bgColor = 'green.50';
                            borderColor = 'green.400';
                            textColor = 'green.700';
                        } else if (isRejected) {
                            bgColor = 'red.50';
                            borderColor = 'red.400';
                            textColor = 'red.700';
                        } else {
                            // Jarayonda or any other status
                            bgColor = 'yellow.50';
                            borderColor = 'yellow.400';
                            textColor = 'yellow.700';
                        }

                        // Get the comment for this specific department
                        let deptComment = '';
                        const paramCode = String(app?.parameter?.code || '');

                        if (it.name === 'Tarmoq texnologiyalari bo\'limi') {
                            deptComment = app.tt_comment || '';
                        } else if (it.name === 'Ta\'lim sifati bo\'limi') {
                            deptComment = app.ts_comment || '';
                        } else if (it.name === 'O\'quv bo\'limi') {
                            if (it.st !== 0 && it.st !== undefined) {
                                deptComment = app.ov_comment || '';
                            }
                        } else if (it.name === 'Ma\'naviyat bo\'limi') {
                            deptComment = app.mn_comment || '';
                        } else if (it.name === 'Marketing bo\'limi') {
                            deptComment = app.mn_comment || '';
                        } else if (it.name === 'Ilmiy bo\'lim') {
                            deptComment = app.ts_comment || '';
                        } else if (it.name === 'Xalqaro hamkorlik bo\'limi') {
                            deptComment = app.tt_comment || '';
                        }

                        return (
                            <Box
                                key={idx}
                                p={2}
                                bg={bgColor}
                                borderLeft="3px solid"
                                borderColor={borderColor}
                                borderRadius={4}
                                display="grid"
                                gridTemplateColumns={deptComment ? "1fr auto 1fr" : "1fr auto"}
                                alignItems="center"
                                gap={3}
                            >
                                <Text fontSize="sm" fontWeight={600} color="gray.700">
                                    {it.name}
                                </Text>
                                <Text fontSize="sm" fontWeight={700} color={textColor} textAlign="center">
                                    {status}
                                </Text>
                                {deptComment && (
                                    <Text fontSize="sm" color={isRejected ? "red.700" : "green.700"} fontStyle="italic" textAlign="right">
                                        {deptComment}
                                    </Text>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    } catch {
        return null;
    }
};

const ProRectorTeacherApps = () => {
    const { teacherId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const toast = useToast();
    const parameterCode = searchParams.get('parameter_code');
    const kafedraNameFromUrl = searchParams.get('kafedra');

    const [applications, setApplications] = useState([]);
    const [teacherInfo, setTeacherInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (teacherId) {
            fetchData();
        }
    }, [teacherId, parameterCode]);

    const fetchData = async () => {
        try {
            setLoading(true);
            console.log(`[DrillDown] Fetching data for teacherId: ${teacherId}, parameterCode: ${parameterCode}`);

            // 1. Fetch applications for this teacher
            let url = `api/v1/application/list/?user_id=${teacherId}`;
            if (parameterCode) {
                url += `&parameter_code=${parameterCode}`;
            }

            const res = await Axios().get(url);
            console.log("[DrillDown] API Response:", res?.data);

            let allApps = [];
            if (res?.data) {
                if (Array.isArray(res.data.applications)) {
                    allApps = res.data.applications;
                } else if (Array.isArray(res.data)) {
                    allApps = res.data;
                }
            }

            console.log(`[DrillDown] Total apps processed: ${allApps.length}`);

            // Filter for approved applications
            const approvedApps = allApps.filter(app => app.status === 4);
            setApplications(approvedApps);
            console.log(`[DrillDown] Approved apps: ${approvedApps.length}`);

            // 2. Determine teacher info
            let info = null;

            // Try to get info from the apps first
            if (allApps.length > 0) {
                const appUser = allApps[0].user;
                info = {
                    first_name: appUser.first_name,
                    last_name: appUser.last_name,
                    partition_name: allApps[0].partition__name || kafedraNameFromUrl
                };
                console.log("[DrillDown] Teacher info from apps:", info);
            }

            // If still no info, try user-results
            if (!info) {
                console.log("[DrillDown] No info from apps, trying user-results...");
                const userRes = await Axios().get(`api/v1/account/user-results/`);
                const users = userRes?.data || [];
                const foundUser = users.find(u => u.id === parseInt(teacherId));
                if (foundUser) {
                    info = {
                        first_name: foundUser.first_name,
                        last_name: foundUser.last_name,
                        partition_name: foundUser.partition__name
                    };
                    console.log("[DrillDown] Teacher info from user-results:", info);
                }
            }

            setTeacherInfo(info);

            if (!info && allApps.length === 0) {
                console.warn("[DrillDown] No data found for this teacher ID");
            }

        } catch (error) {
            console.error("[DrillDown] Error in fetchData:", error);
            toast({
                title: "Xatolik",
                description: "Ma'mulotlarni yuklashda xatolik yuz berdi.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };


    if (loading) {
        return (
            <Layout>
                <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <Spinner size="xl" color="blue.500" thickness="4px" />
                </Box>
            </Layout>
        );
    }

    return (
        <Layout>
            <Box p={5}>
                <Flex align="center" mb={5}>
                    <Button
                        leftIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        variant="solid"
                        bg="#003366"
                        color="white"
                        _hover={{ bg: "#002244" }}
                        mr={4}
                    >
                        Orqaga
                    </Button>
                    <Heading size="lg" color="#003366">O'qituvchi Arizalari</Heading>
                </Flex>

                <Card p={6} boxShadow="xl" borderRadius="xl" border="1px solid" borderColor="gray.100" mb={6}>
                    <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "start", md: "center" }}>
                        <Box>
                            <Text fontSize="sm" color="gray.500" fontWeight="bold" letterSpacing="wider">O'QITUVCHI:</Text>
                            <Heading size="md" color="blue.700" mt={1}>
                                {teacherInfo ? `${teacherInfo.first_name} ${teacherInfo.last_name}` : "Noma'lum o'qituvchi"}
                            </Heading>
                            <Text color="gray.600" mt={1}>
                                {teacherInfo?.kafedra_name || kafedraNameFromUrl || "Kafedra ko'rsatilmadi"}
                            </Text>
                        </Box>
                        {parameterCode && (
                            <Box mt={{ base: 4, md: 0 }} p={3} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderColor="blue.500">
                                <Text fontSize="xs" color="blue.600" fontWeight="bold">SARALANGAN KPI:</Text>
                                <Text fontWeight="bold" color="blue.800">{parameterCode}</Text>
                            </Box>
                        )}
                    </Flex>
                </Card>

                <Card p={6} boxShadow="xl" borderRadius="xl" border="1px solid" borderColor="gray.100">
                    <Heading size="md" mb={6} color="#003366">Ball to'plangan arizalar ro'yxati</Heading>

                    <VStack align="stretch" spacing={4}>
                        {applications.length > 0 ? (
                            applications.map((app) => (
                                <Box
                                    key={app.id}
                                    p={4}
                                    borderRadius={8}
                                    style={{ background: isKafedraPoint(app) ? 'transparent' : statusBg(app.status, app) }}
                                    border={isKafedraPoint(app) ? 'none' : "1px solid #e2e8f0"}
                                    position="relative"
                                >
                                    <Box mb={2} display="flex" justifyContent="space-between" alignItems="flex-start" gap={3}>
                                        <Box flex={1}>
                                            <Text fontWeight="bold" color="blue.600" mb={2}>KPI {app.parameter?.code}</Text>
                                            {renderWork(app)}
                                        </Box>
                                        <Box textAlign="right">
                                            <Box
                                                bg="green.100"
                                                px={3}
                                                py={1}
                                                borderRadius="md"
                                                border="1px solid"
                                                borderColor="green.300"
                                                mb={2}
                                            >
                                                <Text fontSize="lg" fontWeight={700} color="green.700">
                                                    +{parseFloat(app.ball).toFixed(2)} ball
                                                </Text>
                                            </Box>
                                            <Text fontSize="xs" color="gray.500">
                                                {new Date(app.created_at).toLocaleDateString()}
                                            </Text>
                                        </Box>
                                    </Box>

                                    {app.files && app.files.length > 0 && (
                                        <Box mb={2} mt={2}>
                                            <Text fontWeight={600} mb={1} fontSize="sm">Fayllar:</Text>
                                            {app.files.map((file, idx) => (
                                                <Box key={idx} mb={1}>
                                                    <Link href={`${FILE_URL}${file.file}`} isExternal color="blue.500" fontSize="sm" display="flex" alignItems="center">
                                                        <Icon as={ExternalLinkIcon} mr={1} /> {file.name || 'Faylni ko\'rish'}
                                                    </Link>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {!isKafedraPoint(app) && renderDeptStripe(app)}
                                </Box>
                            ))
                        ) : (
                            <Box textAlign="center" p={12}>
                                <Text color="gray.400" fontStyle="italic">
                                    Ushbu o'qituvchi uchun ball to'plangan arizalar topilmadi.
                                </Text>
                            </Box>
                        )}
                    </VStack>
                </Card>
            </Box>
        </Layout>
    );
};

export default ProRectorTeacherApps;
